import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  TextField,
  Grid,
  CircularProgress,
} from "@material-ui/core";
import moment from "moment";
import VesselList from "./vesselList";
import CargoList from "./cargoList";
import { fetchCargoDetails } from "../MatrixCurve/action";
import { userFleets } from "../actions";
import { toast } from "react-toastify";

const ContractSelectionStepper = ({
  modelType,
  subTab,
  vesselContractDetails,
  cargoContractDetails,
  handleModalPagination,
  modalVesselFilters,
  modalCargoFilters,
  onClickVessel,
  onClickCargo,
  selectedVessel,
  selectedCargo,
  loader,
  submitting,
  setLoader,
  onSubmitConfirm,
  onCloseSaveCargoVesselModal,
  currentContract,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [contractType, setContractType] = useState("");
  const [estimateData, setEstimateData] = useState({
    comments: "",
    contractName: "",
  });

  const [cargoList, setCargoList] = useState([]);
  const [vesselList, setVesselList] = useState([]);
  const [isListLoading, setIsListLoading] = useState(false);

  const fetchCargoList = async () => {
    setIsListLoading(true);
    try {
      const response = await fetchCargoDetails();
      setCargoList(response?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setIsListLoading(false);
    }
  };

  const fetchVesselList = async () => {
    setIsListLoading(true);
    try {
      const response = await userFleets();
      setVesselList(response?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setIsListLoading(false);
    }
  };

  useEffect(() => {
    fetchCargoList();
    fetchVesselList();
  }, []);

  const handleContractTypeChange = (event) => {
    setContractType(event.target.value);
  };

  const getSteps = () => {
    if (modelType === "vessel") {
      return ["Select Vessel", "Select Cargo", "Estimate Details"];
    }

    if (modelType === "vessel-cargo") {
      if (subTab === "1") {
        return ["Select Vessel", "Estimate Details"];
      } else if (subTab === "2") {
        return ["Select Cargo", "Estimate Details"];
      }
    } else if (modelType === "general") {
      const steps = [];
      steps.push("Select Operation Type");

      if (contractType === "vessel-cargo") {
        if (subTab === "1") {
          steps.push("Select Vessel");
        } else if (subTab === "2") {
          steps.push("Select Cargo");
        }
      }

      if (contractType === "cargo-cargo") {
        steps.push("Select Vessel");
      }

      steps.push("Estimate Details");
      return steps;
    }

    return ["Estimate Details"];
  };

  const steps = getSteps();

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleEstimateDataChange = (e) => {
    const { name, value } = e.target;
    setEstimateData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmitEstimate = () => {
    if (!estimateData.contractName) {
      toast.error("Contract Name is required");
      return;
    }
    let vesselContractId, cargoContractId;

    if (modelType === "vessel") {
      vesselContractId = selectedVessel?._id;
      cargoContractId = selectedCargo?._id;
    } else if (modelType === "cargo") {
      vesselContractId = currentContract?._id;
      cargoContractId = selectedCargo?._id;
    } else if (modelType === "vessel-cargo") {
      if (subTab === "1") {
        vesselContractId = selectedVessel?._id;
        cargoContractId = currentContract?._id;
      } else if (subTab === "2") {
        vesselContractId = currentContract?._id;
        cargoContractId = selectedCargo?._id;
      }
    } else if (modelType === "general") {
      if (contractType === "vessel-cargo") {
        if (subTab === "1") {
          vesselContractId = selectedVessel?._id;
          cargoContractId = currentContract?._id;
        } else if (subTab === "2") {
          vesselContractId = currentContract?._id;
          cargoContractId = selectedCargo?._id;
        }
      } else if (
        contractType === "cargo-cargo") {
        vesselContractId = selectedVessel?._id;
        cargoContractId = currentContract?._id;
      } else if (
        contractType === "vessel-vessel"
      ) {
        vesselContractId = currentContract?._id;
        cargoContractId = currentContract?._id;
      }
    }

    const finalData = {
      ...estimateData,
      contractType: contractType || currentContract?.contractType,
      ...(modelType === "vessel" || modelType === "general"
        ? {
          vendorCargo: currentContract?.cargoDetails?.customerCargo || currentContract?.cargoDetails,
          vesselData: selectedVessel,
          cargoContractId: cargoContractId || currentContract?._id,
          vesselContractId: vesselContractId || currentContract?._id,
        }
        : {
          cargoContractId,
          vesselContractId,
        }),
      estimationDate: moment.utc(estimateData.estimationDate).toISOString(),
    };

    onSubmitConfirm(finalData);
  };

  const isSelectionMade = () => {
    if (activeStep === 0 && modelType === "general") {
      return !!contractType;
    }

    if (
      modelType === "vessel-cargo" ||
      (modelType === "general" && contractType === "vessel-cargo")
    ) {
      if (subTab === "1") return selectedVessel;
      if (subTab === "2") return selectedCargo;
    }

    if (modelType === "vessel") {
      if (activeStep === 0) return selectedVessel;
      if (activeStep === 1) return selectedCargo;
    }

    if (modelType === "general" && contractType === "cargo-cargo") {
      if (activeStep === 1) return selectedVessel;
    }

    return true;
  };

  const isSkippableStep =
    modelType === "general" &&
    contractType === "cargo-cargo" &&
    steps[activeStep] === "Select Vessel";

  const renderContractTypeSelection = () => {
    return (
      <div className="w-full flex justify-center items-center h-full">
        <div className="mb-3">
          <label className="block text-gray-700 font-medium mb-2">
            Select Operation Type
          </label>

          <div className="flex flex-col sm:flex-row gap-3" role="radiogroup">
            {subTab === "1" ? (
              <>
                <label
                  className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all
                    ${contractType === "cargo-cargo"
                      ? "bg-[#EEF3F9] border-[#313C4C] text-[#313C4C]"
                      : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                    }`}
                >
                  <input
                    type="radio"
                    name="contractType"
                    value="cargo-cargo"
                    checked={contractType === "cargo-cargo"}
                    onChange={handleContractTypeChange}
                    className="h-4 w-4 accent-[#313C4C] focus:ring-[#313C4C]"
                  />
                  <span className="ml-2">Cargo-Cargo</span>
                </label>

                <label
                  className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all
                    ${contractType === "vessel-cargo"
                      ? "bg-[#EEF3F9] border-[#313C4C] text-[#313C4C]"
                      : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                    }`}
                >
                  <input
                    type="radio"
                    name="contractType"
                    value="vessel-cargo"
                    checked={contractType === "vessel-cargo"}
                    onChange={handleContractTypeChange}
                    className="h-4 w-4 accent-[#313C4C] focus:ring-[#313C4C]"
                  />
                  <span className="ml-2">Vessel-Cargo</span>
                </label>
              </>
            ) : (
              <>
                <label
                  className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all
                    ${contractType === "vessel-vessel"
                      ? "bg-[#EEF3F9] border-[#313C4C] text-[#313C4C]"
                      : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                    }`}
                >
                  <input
                    type="radio"
                    name="contractType"
                    value="vessel-vessel"
                    checked={contractType === "vessel-vessel"}
                    onChange={handleContractTypeChange}
                    className="h-4 w-4 accent-[#313C4C] focus:ring-[#313C4C]"
                  />
                  <span className="ml-2">Vessel-Vessel</span>
                </label>

                <label
                  className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all
                    ${contractType === "vessel-cargo"
                      ? "bg-[#EEF3F9] border-[#313C4C] text-[#313C4C]"
                      : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                    }`}
                >
                  <input
                    type="radio"
                    name="contractType"
                    value="vessel-cargo"
                    checked={contractType === "vessel-cargo"}
                    onChange={handleContractTypeChange}
                    className="h-4 w-4 accent-[#313C4C] focus:ring-[#313C4C]"
                  />
                  <span className="ml-2">Vessel-Cargo</span>
                </label>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderStepContent = (step) => {
    if (modelType === "vessel") {
      if (step === 0) {
        return (
          <>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Please select one vessel to create an estimate
            </Typography>
            <VesselList
              setLoader={setLoader}
              vesselContracts={vesselList?.list || vesselList || []}
              onChangeVesselPagination={handleModalPagination}
              vesselFilters={modalVesselFilters}
              onClickVessel={onClickVessel}
              selectedVessel={selectedVessel}
              isLoading={isListLoading}
              modelType={modelType}
            />
          </>
        );
      }
      if (step === 1) {
        return (
          <>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Please select one cargo to create an estimate
            </Typography>
            <CargoList
              setLoader={setLoader}
              cargoContracts={cargoList?.list || cargoList || []}
              onChangeCargoPagination={handleModalPagination}
              cargoFilters={modalCargoFilters}
              onClickCargo={onClickCargo}
              selectedCargo={selectedCargo}
              isLoading={isListLoading}
              modelType={modelType}
            />
          </>
        );
      }
    }

    if (modelType === "general" && contractType === "cargo-cargo") {
      if (step === 1) {
        return (
          <>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Select the vessel contract
            </Typography>
            <VesselList
              setLoader={setLoader}
              vesselContracts={vesselList?.list || vesselList || []}
              onChangeVesselPagination={handleModalPagination}
              vesselFilters={modalVesselFilters}
              onClickVessel={onClickVessel}
              selectedVessel={selectedVessel}
              isLoading={isListLoading}
              modelType={modelType}
            />
          </>
        );
      }
    }

    if (modelType === "general" && step === 0) {
      return renderContractTypeSelection();
    }

    if (step === steps.findIndex((s) => s === "Select Vessel")) {
      return (
        <>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Please select one vessel to create an estimate
          </Typography>
          <VesselList
            setLoader={setLoader}
            vesselContracts={vesselContractDetails}
            onChangeVesselPagination={handleModalPagination}
            vesselFilters={modalVesselFilters}
            onClickVessel={onClickVessel}
            selectedVessel={selectedVessel}
            isLoading={isListLoading}
            contractType={contractType}
            modelType={modelType}
          />
        </>
      );
    }

    if (step === steps.findIndex((s) => s === "Select Cargo")) {
      return (
        <>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Please select one cargo to create an estimate
          </Typography>
          <CargoList
            setLoader={setLoader}
            cargoContracts={cargoContractDetails}
            onChangeCargoPagination={handleModalPagination}
            cargoFilters={modalCargoFilters}
            onClickCargo={onClickCargo}
            selectedCargo={selectedCargo}
            isLoading={isListLoading}
            contractType={contractType}
            modelType={modelType}
          />
        </>
      );
    }

    if (step === steps.findIndex((s) => s === "Estimate Details")) {
      return (
        <Paper
          elevation={0}
          style={{ padding: 16 }}
          className="max-w-2xl mx-auto"
        >
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Estimation Date"
                variant="outlined"
                value={moment().format("DD-MM-YYYY")}
                disabled
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contract Name"
                variant="outlined"
                name="contractName"
                value={estimateData.contractName}
                onChange={handleEstimateDataChange}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Comments"
                variant="outlined"
                name="comments"
                value={estimateData.comments}
                onChange={handleEstimateDataChange}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </Paper>
      );
    }

    return null;
  };

  const shouldShowNext = () => activeStep < steps.length - 1;
  const shouldShowCreate = () => activeStep === steps.length - 1;

  return (
    <Box className="flex flex-col justify-center">
      <Stepper
        activeStep={activeStep}
        className="stepper-background"
        style={{ background: "#fff" }}
        alternativeLabel
      >
        {steps.map((label) => (
          <Step style={{ background: "#fff" }} key={label}>
            <StepLabel style={{ background: "#fff" }}>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box mb={1}>{renderStepContent(activeStep)}</Box>

      <Box className="flex items-center mt-3 justify-between">

        <div>
          {activeStep > 0 && (
            <Button onClick={handleBack} disabled={submitting} variant="outlined" color="primary">
              Back
            </Button>
          )}
        </div>

        <div className="flex items-center">

          {isSkippableStep && (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleNext}
              style={{ marginRight: 8 }}
            >
              Skip
            </Button>
          )}

          {shouldShowNext() && (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleNext}
              disabled={!isSelectionMade()}
            >
              Next Page
            </Button>
          )}

          {shouldShowCreate() && (
            <>
              <Button
                variant="contained"
                color="primary"
                disabled={submitting}
                onClick={handleSubmitEstimate}
              >
                {submitting ? <span className="flex items-center justify-center gap-3"><CircularProgress size={24} color="inherit" /> Creating Estimate...</span> : "Create Estimate"}
              </Button>
            </>
          )}
        </div>
      </Box>
    </Box>
  );
};

export default ContractSelectionStepper;
